import type {ReactElement} from 'react';
import {Text, TextSize, TextType} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {FlatIconColor, FlatIconColorType, IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import {formatTime, getDateAndMonthStringWithOrdinal, isToday} from '@/utils/date.util';
import {AvatarPlaceholderIcon} from '@Components/avatar-placeholder-icon/avatar-placeholder-icon';
import {AvatarBorderThickness, AvatarSize} from '@Components/avatar-placeholder-icon/avatar-placeholder-icon.types';
import {getThumbnailIconProps} from '../../poster-editor-version-history-panel-helper';
import styles from './version-list-item.module.scss';
import type {PosterVersionData} from '@Components/poster-editor/library/poster-editor-library';


export interface VersionListItemProps {
  version: PosterVersionData;
  index: number;
  isSelected: boolean;
  isLoading: boolean;
  onVersionSelect: (index: number) => Promise<void>;
}

export function VersionListItem({version, index, isSelected, isLoading, onVersionSelect}: VersionListItemProps): ReactElement {
  const handleClick = (): void => {
    if (!isLoading) {
      void onVersionSelect(index);
    }
  };

  const getVersionTitle = (): string => {
    if (isLoading) return window.i18next.t('pmwjs_fetching_version');

    if (index === 0) {
      const versionDate = new Date(version.createdOn * 1000);

      if (isToday(versionDate)) {
        return `${window.i18next.t('pmwjs_current_version')}, ${formatTime(version.createdOn)}`;
      } else {
        return `${window.i18next.t('pmwjs_current_version')}, ${getDateAndMonthStringWithOrdinal(versionDate)}`;
      }
    }

    return `${formatTime(version.createdOn)}, ${getDateAndMonthStringWithOrdinal(new Date(version.createdOn * 1000))}`;
  };

  const getTextColorClass = (): string => {
    return isSelected ? 'content-primary' : 'content-body';
  };

  const renderIcon = (): ReactElement => (
    <div className="spacing-p-t-2">
      <div className={`flexbox flex-center _position-relative ${styles.versionIconWrapper}`}>
        {isLoading ? (
          <div className={styles.loaderContainer}>
            <CircularProgressLoader color={LOADER_COLOR.PRIMARY_DARK} size={LOADER_SIZE.XXSMALL} />
          </div>
        ) : (
          <Icon
            icon={isSelected ? 'icon-current-version' : 'icon-old-version'}
            className='content-sub-text'
            size={IconSize.SIZE_ICON_12}
            type={IconType.FLAT}
            flatIconType={{
              color: isSelected ? FlatIconColor.PRIMARY : FlatIconColor.NONE,
              type: FlatIconColorType.DEFAULT,
            }}
            shape={IconShape.CIRCLE}
          />
        )}
      </div>
    </div>
  );

  const renderCreatorInfo = (): ReactElement => {
    if (isLoading) {
      return (
        <Text
          val={`${formatTime(version.createdOn)}, ${getDateAndMonthStringWithOrdinal(new Date(version.createdOn * 1000))}`}
          className="content-primary"
          size={TextSize.XXSMALL}
        />
      );
    }

    return (
      <div className="flexbox flex-row-align-center gap-1">
        <AvatarPlaceholderIcon
          placeholderLetter={getThumbnailIconProps(version.creatorName).placeholderLetter}
          size={AvatarSize.XSMALL}
          colorClass={getThumbnailIconProps(version.creatorName).colorClass}
          borderThickness={AvatarBorderThickness.THIN}
        />
        <Text val={version.creatorName ?? ''} type={TextType.BODY} size={TextSize.XXSMALL} className="content-sub-text" />
      </div>
    );
  };

  const renderContent = (): ReactElement => (
    <div className={`flexbox flex-column-justify-start flex-items-start flex-1 ${styles.versionContent}`}>
      <Text
        val={getVersionTitle()}
        type={TextType.BODY}
        className={`${styles.versionContentText} ${getTextColorClass()}`}
        size={TextSize.XSMALL}
        bold={isSelected}
        title={getVersionTitle()}
      />
      <div className="flexbox flex-row-align-center gap-1">{renderCreatorInfo()}</div>
    </div>
  );

  return (
    <li className={`_cursor-pointer ${styles.versionItem} ${isSelected ? styles.selected : ''}`} onClick={handleClick}>
      <div className={`flexbox flex-items-start ${styles.versionItemContent}`}>
        {renderIcon()}
        {renderContent()}
      </div>
    </li>
  );
}
